import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';
import { APP_CONFIG } from '@/config/app';
import { clearPendingOAuthLogin } from '@/lib/loginTracking';
import { clearOAuthParamsFromUrl, describeOAuthError, readOAuthError } from '@/lib/oauthReturn';
import { AuthDivider, GoogleButton } from '@/components/auth/GoogleButton';
import { useGoogleSignInEnabled } from '@/hooks/useGoogleSignInEnabled';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

const signupSchema = loginSchema.extend({
  fullName: z.string().min(2, 'Name must be at least 2 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export default function Auth() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const { signIn, signInWithGoogle, user } = useAuth();
  const googleEnabled = useGoogleSignInEnabled();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    const oauthError = readOAuthError();
    if (!oauthError) return;
    clearPendingOAuthLogin();
    clearOAuthParamsFromUrl();
    toast({
      title: 'Could not sign in with Google',
      // handle_new_user refuses any new account without an invite.
      description: describeOAuthError(
        oauthError,
        `There's no ${APP_CONFIG.name} account for that Google address. Sign in with your email and password, or ask your administrator for an invite.`,
      ),
      variant: 'destructive',
    });
  }, [toast]);

  const handleGoogle = async () => {
    setIsGoogleLoading(true);
    const { error } = await signInWithGoogle();
    // On success the browser is already leaving for Google.
    if (error) {
      clearPendingOAuthLogin();
      setIsGoogleLoading(false);
      toast({
        title: 'Could not sign in with Google',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const validated = loginSchema.parse({ email, password });
      const { error } = await signIn(validated.email, validated.password);

      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          toast({
            title: 'Login failed',
            description: 'Invalid email or password',
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Error',
            description: error.message,
            variant: 'destructive',
          });
        }
      } else {
        toast({
          title: 'Welcome back!',
          description: 'You have successfully signed in.',
        });
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: 'Validation error',
          description: error.errors[0].message,
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Brand panel — rail colour, hidden below lg */}
      <div className="hidden bg-sidebar p-12 text-sidebar-foreground lg:flex lg:w-1/2 lg:flex-col">
        <div className="flex items-center gap-3">
          <img src="/logo/logo-mark-white.svg" alt={APP_CONFIG.name} className="h-10 w-10" />
          <span className="text-lg font-semibold tracking-tight">{APP_CONFIG.name}</span>
        </div>

        <div className="my-auto max-w-[480px]">
          <h2 className="text-[36px] font-semibold leading-[1.15] tracking-[-0.02em]">
            Every lead, job and dollar, in one clear view.
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-sidebar-foreground/70">
            Calls, leads, jobs and invoices — connected, so nothing slips through.
          </p>
        </div>
      </div>

      {/* Sign-in form */}
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-[380px]">
          <div className="mb-8 lg:hidden">
            <img src="/logo/logo-mark.svg" alt={APP_CONFIG.name} className="h-10 w-10 dark:hidden" />
            <img src="/logo/logo-mark-dark.svg" alt={APP_CONFIG.name} className="hidden h-10 w-10 dark:block" />
          </div>

          <h1 className="text-2xl font-semibold tracking-[-0.02em]">Sign in</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Enter your credentials to access your account
          </p>

          {googleEnabled && (
            <>
              <GoogleButton
                className="mt-6"
                onClick={handleGoogle}
                isLoading={isGoogleLoading}
                disabled={isLoading}
              />

              <AuthDivider className="mt-6" label="or sign in with email" />
            </>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
            </div>

            <Button type="submit" className="h-[42px] w-full" disabled={isLoading}>
              {isLoading ? 'Loading...' : 'Sign in'}
            </Button>

            <Button
              type="button"
              variant="link"
              className="w-full"
              onClick={() => navigate('/forgot-password')}
            >
              Forgot password?
            </Button>
          </form>

          <div className="mt-6 border-t pt-4 text-center text-[13px] text-muted-foreground">
            <p>This is an invite-only platform.</p>
            <p>Contact your administrator for access.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
